import React from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"

import { useAppDispatch, useAppSelector } from "redux/hooks"
import { openDialog } from "redux/features/dialog/dialogSlice"
import { selectSettingsById } from "redux/features/settings/settingsSlice"
import { formatDate, formatMoney } from "helpers/formatters"
import {
  isDebtContractDownload,
  isIssuesCreate,
  isPaymentRequestsCreate,
  isPaymentSchedulesShow,
  isFreshFinance,
  isPunkfinance,
} from "helpers/features"
import { PAYMENT_PAY_MODAL, SEPA_PAYMENT_PAY_MODAL } from "config"
import Doughnut from "components/ui/Doughnut"
import Title from "components/Debt/Title"
import Manager from "components/Debt/Manager"
import Summary from "components/Debt/Summary"
import Download from "components/Debt/Download"
import Information from "components/ui/InfoList"
import Card from "components/ui/CardAccordion"
import {
  CollapsedAside,
  Balance,
  CollapsedActions,
  CollapsedLightButton,
  Container,
  Aside,
  Content,
  Info,
  ActionList,
  ActionListItem,
  LightButton,
  PrimaryButton,
} from "components/Debt/elements"
import { DebtType, SettingsType } from "types"

type Props = React.HTMLAttributes<HTMLDivElement> & {
  item: DebtType
}

const DebtPaymentSchedule = ({ item }: Props) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const settings = useAppSelector(
    (state) => selectSettingsById(state, 0) as SettingsType,
  )

  const {
    id,
    balance,
    start_date,
    total_paid_percent,
    unpaid_total_sum,
    payment_schedule,
    creditor,
    manager,
    company,
  } = item

  const general = [
    {
      label: t("features.debt.list.start_date.title"),
      value: formatDate(start_date),
      visible: true,
    },
    {
      label: t("features.debt.list.creditor.title"),
      value: creditor?.name,
      visible: isFreshFinance(),
    },
    {
      label: t("features.debt.list.company.title"),
      value: company?.legal_name,
      visible: isFreshFinance(),
    },
    {
      label: t("features.debt.list.balance.title"),
      value: formatMoney(balance),
      visible: isPunkfinance(),
    },
    {
      label: t("features.debt.list.next_payment.title", {
        date: formatDate(
          payment_schedule?.next_payment_schedule_invoice?.due_date,
        ),
      }),
      value: formatMoney(
        payment_schedule?.next_payment_schedule_invoice?.total_sum,
      ),
      visible: true,
    },
  ].filter((item) => item.value && item.visible)

  const handlePaymentScheduleView = () => {
    navigate(`/debts/${id}/payment_schedules/${payment_schedule?.id}`)
  }

  const handlePaymentCreate = (
    event: React.SyntheticEvent,
    amount: number | undefined,
  ): void => {
    event.preventDefault()
    event.stopPropagation()

    dispatch(
      openDialog({
        type: isPaymentRequestsCreate(settings?.features)
          ? PAYMENT_PAY_MODAL
          : SEPA_PAYMENT_PAY_MODAL,
        content: {
          debtId: id,
          paymentScheduleId: payment_schedule?.id,
          amount: amount,
        },
      }),
    )
  }

  const handleIssueCreate = () => {
    navigate(`/debts/${id}/issues/new`)
  }

  return (
    <Card
      collapsed={
        <>
          <CollapsedAside>
            <Title compact debt={item} />
            <Balance variant="h4">
              {formatMoney(isFreshFinance() ? unpaid_total_sum : balance)}
            </Balance>
          </CollapsedAside>
          <CollapsedActions>
            {isPaymentRequestsCreate(settings?.features) && (
              <CollapsedLightButton
                onClick={(event) =>
                  handlePaymentCreate(
                    event,
                    payment_schedule?.next_payment_schedule_invoice?.total_sum,
                  )
                }
              >
                {t("features.debt.actions.payment.next.title", {
                  amount: formatMoney(
                    payment_schedule?.next_payment_schedule_invoice?.total_sum,
                  ),
                })}
              </CollapsedLightButton>
            )}
          </CollapsedActions>
        </>
      }
      expanded={
        <Container>
          <Aside>
            {isFreshFinance() ? (
              <Doughnut
                top={t("features.debt.doughnut.remain.title")}
                amount={formatMoney(unpaid_total_sum)}
                value={total_paid_percent}
              />
            ) : (
              <Doughnut
                top={t("features.debt.doughnut.balance.title")}
                amount={formatMoney(balance)}
                value={total_paid_percent}
              />
            )}
            <Summary item={item} />
          </Aside>
          <Content>
            <Info>
              <Title debt={item} />
              <Manager dense manager={manager} />
              <Information dense items={general} />
            </Info>
            <ActionList>
              {isPaymentRequestsCreate(settings?.features) && (
                <ActionListItem>
                  <PrimaryButton
                    onClick={(event) =>
                      handlePaymentCreate(
                        event,
                        payment_schedule?.next_payment_schedule_invoice
                          ?.total_sum,
                      )
                    }
                  >
                    {t("features.debt.actions.payment.next.title", {
                      amount: formatMoney(
                        payment_schedule?.next_payment_schedule_invoice
                          ?.total_sum,
                      ),
                    })}
                  </PrimaryButton>
                </ActionListItem>
              )}
              {isPaymentSchedulesShow(settings?.features) && (
                <ActionListItem>
                  <LightButton onClick={handlePaymentScheduleView}>
                    {t("features.payment_schedule.stage.active.title")}
                  </LightButton>
                </ActionListItem>
              )}
              {isIssuesCreate(settings?.features) && (
                <ActionListItem>
                  <LightButton onClick={handleIssueCreate}>
                    {t("features.debt.actions.issue.create.title")}
                  </LightButton>
                </ActionListItem>
              )}
              {isDebtContractDownload(settings?.features) && (
                <ActionListItem>
                  <Download debtId={id}>
                    {t("features.debt.actions.contract.download.title")}
                  </Download>
                </ActionListItem>
              )}
            </ActionList>
          </Content>
        </Container>
      }
    />
  )
}

export default DebtPaymentSchedule
